import type { InternetAccessAccount } from '../../api/types.js';
import { formatMinutesSeconds } from '../../core/format.js';
import {
  splitTickingRemaining,
  useTickingConsumedSeconds,
  useTickingRemainingSeconds,
} from '../../core/ticking.js';

/**
 * Port of Android's `InternetAccessSettingsCard` — the shared
 * internet-time balance for one child (see docs/INTERNET_ACCESS.md).
 *
 * `account` is null only while still loading; a child with no top-ups yet
 * still gets a real account back from the backend, just with a zero
 * balance, so there is no "sensible defaults" case to render around here.
 */
export function InternetAccessCard({
  account,
  savingTopUp,
  savingDeductBonus,
  savingForceBlocked,
  savingReset,
  onTopUp,
  onDeductBonus,
  onSetForceBlocked,
  onReset,
}: {
  account: InternetAccessAccount | undefined;
  savingTopUp: boolean;
  savingDeductBonus: boolean;
  savingForceBlocked: boolean;
  savingReset: boolean;
  onTopUp: () => void;
  onDeductBonus: () => void;
  onSetForceBlocked: (forceBlocked: boolean) => void;
  onReset: () => void;
}) {
  // Tick locally between polls while a device is actually spending the
  // balance. Irrelevant while `account` is still undefined (loading): the
  // markup below shows "Lädt…" regardless of these values in that case.
  const ticking = account?.onlineDeviceId != null;
  const remainingSeconds = useTickingRemainingSeconds(account?.remainingSeconds ?? 0, ticking);
  const consumedSeconds = useTickingConsumedSeconds(account?.consumedSeconds ?? 0, ticking);
  // Derived from the ticking total above, not ticked independently — see
  // `splitTickingRemaining`.
  const { dailyRemainingSeconds, bonusRemainingSeconds } = splitTickingRemaining(
    account?.dailyRemainingSeconds ?? 0,
    account?.bonusRemainingSeconds ?? 0,
    remainingSeconds,
  );

  return (
    <div className="card card--inset">
      <h4 className="card__heading">Internet-Guthaben</h4>

      {account === undefined ? (
        <p className="muted">Lädt…</p>
      ) : (
        <div aria-live="off">
          <p className="balance">
            Tageskontingent: <strong>{formatMinutesSeconds(dailyRemainingSeconds)}</strong>
            {account.onlineDeviceId !== null && <span className="badge badge--online">gerade aktiv</span>}
          </p>
          <p className="muted">Bonus: {formatMinutesSeconds(bonusRemainingSeconds)}</p>
          <p className="muted">Verbraucht: {formatMinutesSeconds(consumedSeconds)}</p>
        </div>
      )}

      <label className="switch">
        <input
          type="checkbox"
          checked={account?.forceBlocked ?? false}
          disabled={account === undefined || savingForceBlocked}
          onChange={(event) => onSetForceBlocked(event.target.checked)}
        />
        <span>Sperren (z.B. Hausaufgaben)</span>
      </label>

      <div className="button-row">
        <button
          type="button"
          className="button button--primary"
          onClick={onTopUp}
          disabled={account === undefined || savingTopUp}
        >
          +5 Min
        </button>
        {/* Mirror of "+5 Min" — removes from the bonus pot only, clamped
            at zero server-side. Disabled once there is nothing left there
            to take back, just as "+5 Min" never needs a disabled state
            since it has no upper bound. Red, matching its subtractive
            nature, and distinct from both other buttons here. */}
        <button
          type="button"
          className="button button--danger"
          onClick={onDeductBonus}
          disabled={account === undefined || savingDeductBonus || bonusRemainingSeconds <= 0}
        >
          −5 Min
        </button>
        {/* Hard reset to the standard daily allowance — discards
            leftover/overdrawn time entirely, unlike "+5 Min" which only
            ever adds. A third, distinct accent for "a different kind of
            action". */}
        <button
          type="button"
          className="button button--tertiary"
          onClick={onReset}
          disabled={account === undefined || savingReset}
        >
          Reset
        </button>
      </div>
    </div>
  );
}
